import styled, { css } from 'styled-components';

import { Colors, Sizes, Variants } from './types';

const ContainedButton = css<{ $hasHover: boolean; $highlighted: boolean }>`
  background: transparent;
  border: none;
  transition: background 200ms;

  &:active {
    background: ${({ theme }) => theme.palette.grey[100]};
    color: ${({ theme }) => theme.palette.text.primary};
  }

  ${({ $hasHover, theme }) =>
    $hasHover &&
    css`
      @media (any-hover: hover) {
        &:hover {
          background: ${theme.palette.grey[100]};
          color: ${theme.palette.grey[700]};
        }
      }
    `};

  ${({ $highlighted, theme }) =>
    $highlighted &&
    css`
      background: ${theme.palette.grey[100]};
      color: ${theme.palette.text.primary};

      &:hover {
        background: ${theme.palette.grey[100]};
        color: ${theme.palette.text.primary};
      }
    `};
`;
const OutlinedButton = css<{ $hasHover: boolean; $highlighted: boolean }>`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  transition: background 200ms;

  &:active {
    background: ${({ theme }) => theme.palette.grey[200]};
  }

  ${({ $hasHover }) =>
    $hasHover &&
    css`
      @media (any-hover: hover) {
        &:hover {
          background: ${({ theme }) => theme.palette.grey[100]};
        }
      }
    `};

  ${({ $highlighted, theme }) =>
    $highlighted &&
    css`
      background: ${theme.palette.grey[100]};
    `};
`;

const ExtraLargeButton = css`
  height: 4rem;
  width: 4rem;

  line-height: 62.5%;
  font-size: 2.4rem;
`;

const LargeButton = css`
  height: 3.2rem;
  width: 3.2rem;

  line-height: 62.5%;
  font-size: 2rem;
`;

const MediumButton = css`
  height: 2.8rem;
  width: 2.8rem;

  border-radius: ${({ theme }) => theme.spacing(2)};

  line-height: 62.5%;
  font-size: 1.8rem;
`;

const SmallButton = css`
  height: 2.2rem;
  width: 2.2rem;

  border-radius: ${({ theme }) => theme.spacing(1.5)};

  line-height: 62.5%;
  font-size: 1.6rem;
`;

const ExtraSmallButton = css`
  height: 1.8rem;
  width: 1.8rem;

  line-height: 62.5%;
  font-size: 1.2rem;
`;

export const Button = styled.button<{
  $variant: Variants;
  $size: Sizes;
  $color: Colors;
  $preventDrag: boolean;
  $hasHover: boolean;
  $highlighted: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  margin: 0;
  padding: 0;

  border-radius: ${({ theme }) => theme.spacing(2)};

  background: transparent;
  cursor: pointer;

  color: inherit;

  ${({ $color, theme }) => {
    switch ($color) {
      case Colors.Default:
        return css`
          color: ${theme.palette.grey[700]};

          &:hover {
            background: ${theme.palette.grey[100]};
          }
        `;
    }
  }}

  ${({ $variant }) => {
    switch ($variant) {
      case Variants.Outlined:
        return OutlinedButton;
      default:
        return ContainedButton;
    }
  }};

  ${({ $size }) => {
    switch ($size) {
      case Sizes.ExtraLarge:
        return ExtraLargeButton;
      case Sizes.Large:
        return LargeButton;
      case Sizes.Small:
        return SmallButton;
      case Sizes.ExtraSmall:
        return ExtraSmallButton;
      default:
        return MediumButton;
    }
  }};

  ${({ $preventDrag }) =>
    $preventDrag &&
    css`
      -webkit-app-region: no-drag;
    `};

  &:disabled {
    cursor: default;
    opacity: 0.4;

    &:hover {
      background: initial;
    }
  }
`;

export const IconContainer = styled.span<{ $rotation?: number }>`
  font-size: inherit;
  line-height: inherit;

  transition: transform 200ms;

  ${({ $rotation }) =>
    $rotation &&
    css`
      transform: rotate(${$rotation}deg);
    `};
`;
